import { useLanguage } from "@/context/LanguageContext";
import { ExternalLink } from "lucide-react";

const STEPS = ["New", "Processing", "Shipped", "Out for Delivery", "Delivered"];

export default function OrderTimeline({ order }) {
  const { t, lang } = useLanguage();
  const currentIdx = order.status === "Cancelled" ? -1 : STEPS.indexOf(order.status);
  const history = order.history || [];

  const timeFor = (step) => {
    const h = history.find((x) => x.status === step);
    if (!h) return "";
    try {
      return new Date(h.at).toLocaleString(lang === "hi" ? "hi-IN" : "en-IN", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  return (
    <div data-testid="order-timeline">
      {order.status === "Cancelled" ? (
        <span
          data-testid="order-cancelled-badge"
          className="inline-block border border-red-500/40 bg-red-500/10 px-4 py-2 text-[12px] uppercase tracking-widest text-red-300"
        >
          {t.orderPage.statuses.Cancelled}
        </span>
      ) : (
        <ol className="relative ml-2 border-l hairline">
          {STEPS.map((step, i) => {
            const done = i < currentIdx;
            const current = i === currentIdx;
            const time = timeFor(step);
            return (
              <li key={step} className="relative pb-7 pl-8 last:pb-0">
                <span
                  aria-hidden="true"
                  className={`absolute -left-[7px] top-1 h-3.5 w-3.5 rounded-full border transition-colors ${
                    done || current
                      ? "border-gold bg-gold"
                      : "border-cream/30 bg-obsidian"
                  }`}
                />
                <p
                  className={`font-serif text-lg ${
                    done || current ? "text-gold-light" : "text-cream/40"
                  }`}
                  data-testid={`order-step-${step.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {t.orderPage.statuses[step]}
                  {current && (
                    <span className="ml-3 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-gold align-middle" />
                  )}
                </p>
                {time && <p className="mt-0.5 text-[12px] text-cream/45">{time}</p>}
              </li>
            );
          })}
        </ol>
      )}

      {order.tracking?.activities?.length > 0 && (
        <div className="mt-8" data-testid="order-tracking-activities">
          <h4 className="text-[11px] font-medium uppercase tracking-luxe text-gold/90">
            {t.orderPage.activities}
          </h4>
          <ul className="mt-4 max-h-64 space-y-3 overflow-y-auto pr-2">
            {order.tracking.activities.map((a, i) => (
              <li key={i} className="border-b hairline pb-3 text-sm last:border-0">
                <p className="text-cream/80">{a.activity}</p>
                <p className="mt-1 text-[12px] text-cream/45">
                  {a.date} {a.location ? `· ${a.location}` : ""}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {order.tracking?.track_url && (
        <a
          href={order.tracking.track_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="order-track-url-link"
          className="mt-5 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-gold hover:text-gold-light"
        >
          {t.orderPage.trackUrl}
          <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
        </a>
      )}
    </div>
  );
}
